import { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * StatusChip — single shared chip for status surfaces.
 *
 *   success   green   — live / enabled / available / synced
 *   warning   orange  — offline / captive portal / waiting
 *   danger    red     — failed / destructive
 *   info      blue    — selected / recommended / primary
 *   neutral   gray    — inactive / not granted / idle
 */
export type StatusChipVariant = "success" | "warning" | "danger" | "info" | "neutral";

const variants: Record<StatusChipVariant, string> = {
  success:
    "bg-[var(--roava-green)]/12 text-[var(--roava-green)] ring-1 ring-inset ring-[var(--roava-green)]/25",
  warning:
    "bg-[var(--roava-orange)]/15 text-[var(--roava-orange)] ring-1 ring-inset ring-[var(--roava-orange)]/30",
  danger:
    "bg-[var(--roava-red)]/12 text-[var(--roava-red)] ring-1 ring-inset ring-[var(--roava-red)]/25",
  info:
    "bg-[var(--roava-blue)]/12 text-[var(--roava-blue)] ring-1 ring-inset ring-[var(--roava-blue)]/25",
  neutral:
    "bg-[var(--roava-gray)] text-[var(--roava-text-muted)] ring-1 ring-inset ring-[var(--roava-border)]",
};

export function StatusChip({
  variant = "neutral",
  icon,
  children,
  className,
}: {
  variant?: StatusChipVariant;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold leading-none",
        variants[variant],
        className,
      )}
    >
      {icon}
      <span>{children}</span>
    </span>
  );
}
